<template>
  <div>

    <div class="form">
      <validation-observer
          v-slot="{ handleSubmit }"
          ref="refFormObserver"
      >
        <b-form
            ref="form"
            @submit.stop.prevent="handleSubmit(createOrUpdateLesson($event))"
            class="form validate"
        >

          <!-- Basic Lesson Information -->
          <div class="basic-info-section">
            <h4>Basic Information</h4>

            <div class="select-wrapper">
              <validation-provider
                  v-slot="validationContext"
                  name="title"
                  :rules="{required: true}"
              >
                <b-form-group
                    :label="$t('Lesson Title')"
                    label-for="title"
                >
                  <b-form-input
                      type="text"
                      trim
                      v-model="form.title"
                      :state="getValidationState(validationContext)"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </div>

            <div class="select-wrapper">
              <validation-provider
                  v-slot="validationContext"
                  name="icon"
                  :rules="{required: !form.id}"
              >
                <b-form-group
                    :label="$t('Lesson Icon')"
                    label-for="icon"
                >
                  <b-form-file
                      type="file"
                      trim
                      v-model="form.icon"
                      accept="image/*"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </div>

            <div class="select-wrapper">
              <validation-provider
                  v-slot="validationContext"
                  name="lesson_type"
                  :rules="{required: true}"
              >
                <b-form-group
                    :label="$t('Lesson Type')"
                    label-for="lesson_type"
                >
                  <v-select
                      name="lesson_type"
                      :options="lessonTypes"
                      label="label"
                      v-model="form.lesson_type"
                      :reduce="model => model.value"
                      :state="getValidationState(validationContext)"
                      @input="onLessonTypeChange"
                  >
                  </v-select>
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </div>

            <div class="select-wrapper">
              <validation-provider
                  v-slot="validationContext"
                  name="subject_id"
                  :rules="{required: true}"
              >
                <b-form-group
                    :label="$t('Subject')"
                    label-for="subject_id"
                >
                  <v-select
                      name="subject_id"
                      :options="subjects"
                      label="name"
                      v-model="form.subject_id"
                      :reduce="model => model.id"
                      :state="getValidationState(validationContext)"
                      @input="onSubjectChange"
                  >
                  </v-select>
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </div>

            <div class="select-wrapper">
              <validation-provider
                  v-slot="validationContext"
                  name="program_id"
              >
                <b-form-group
                    :label="$t('Program (Optional)')"
                    label-for="program_id"
                >
                  <v-select
                      name="program_id"
                      :options="filteredPrograms"
                      label="name"
                      v-model="form.program_id"
                      :reduce="model => model.id"
                      placeholder="Select a program..."
                  >
                  </v-select>
                </b-form-group>
              </validation-provider>
            </div>

            <div class="select-wrapper">
              <validation-provider
                  v-slot="validationContext"
                  name="order"
                  :rules="{required: true}"
              >
                <b-form-group
                    :label="$t('Order Number')"
                    label-for="order"
                >
                  <b-form-input
                      type="number"
                      min="1"
                      trim
                      v-model="form.order"
                  />
                </b-form-group>
              </validation-provider>
            </div>

            <div class="select-wrapper">
              <validation-provider
                  v-slot="validationContext"
                  name="difficulty_level"
              >
                <b-form-group
                    :label="$t('Difficulty Level')"
                    label-for="difficulty_level"
                >
                  <v-select
                      name="difficulty_level"
                      :options="difficultyLevels"
                      label="label"
                      v-model="form.difficulty_level"
                      :reduce="model => model.value"
                  >
                  </v-select>
                </b-form-group>
              </validation-provider>
            </div>

            <div class="select-wrapper">
              <validation-provider
                  v-slot="validationContext"
                  name="description"
              >
                <b-form-group
                    :label="$t('Description')"
                    label-for="description"
                >
                  <b-form-textarea
                      rows="3"
                      trim
                      v-model="form.description"
                      placeholder="Describe what this lesson covers..."
                  />
                </b-form-group>
              </validation-provider>
            </div>

            <div class="select-wrapper">
              <validation-provider
                  v-slot="validationContext"
                  name="estimated_duration_minutes"
              >
                <b-form-group
                    :label="$t('Estimated Duration (minutes)')"
                    label-for="estimated_duration_minutes"
                >
                  <b-form-input
                      type="number"
                      min="1"
                      trim
                      v-model="form.estimated_duration_minutes"
                      placeholder="60"
                  />
                </b-form-group>
              </validation-provider>
            </div>

            <div class="select-wrapper">
              <b-form-checkbox
                  v-model="form.is_published"
                  :checked="form.is_published"
              >
                Published
              </b-form-checkbox>
            </div>
          </div>

          <!-- Control Lesson Options -->
          <div v-if="form.lesson_type === 'control'" class="control-lesson-section mt-4">
            <div class="alert alert-info">
              <h5>Control Lesson</h5>
              <p>Control lessons are used for testing and assessment. They can contain tests, essays, and other assessment components.</p>
            </div>

            <b-form-checkbox
                v-model="form.auto_populate"
                :checked="form.auto_populate"
                @input="onAutoPopulateChange"
            >
              Auto-populate with available tests and essays
            </b-form-checkbox>

            <!-- Available Tests Preview -->
            <div v-if="availableTestPacks.length > 0" class="mt-3">
              <h6>Available Tests:</h6>
              <ul class="list-group">
                <li v-for="test in availableTestPacks" :key="test.id" class="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ test.name }}</strong> - {{ test.description }}
                    <br>
                    <small class="text-muted">{{ test.questions_count || 0 }} questions</small>
                  </div>
                  <div v-if="!form.auto_populate">
                    <b-form-checkbox
                        v-model="form.selected_test_packs"
                        :value="test.id"
                    >
                      Include
                    </b-form-checkbox>
                  </div>
                </li>
              </ul>
            </div>

            <!-- Available Essay Questions Preview -->
            <div v-if="availableEssayQuestions.length > 0" class="mt-3">
              <h6>Available Essay Questions:</h6>
              <ul class="list-group">
                <li v-for="question in availableEssayQuestions" :key="question.id" class="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    {{ question.essay_prompt }}
                    <br>
                    <small class="text-muted">{{ question.max_words || 'No limit' }} words, {{ question.points || 0 }} points</small>
                  </div>
                  <div v-if="!form.auto_populate">
                    <b-form-checkbox
                        v-model="form.selected_essay_questions"
                        :value="question.id"
                    >
                      Include
                    </b-form-checkbox>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <!-- Lesson Components Section -->
          <div v-if="form.lesson_type === 'regular'" class="lesson-components-section mt-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5>Lesson Content</h5>
              <b-dropdown text="Add Content" variant="primary" right>
                <b-dropdown-item @click="addComponent('text_input')">
                  <feather-icon icon="TypeIcon" class="mr-50"/>
                  Text Input
                </b-dropdown-item>
                <b-dropdown-item @click="addComponent('text_area')">
                  <feather-icon icon="AlignLeftIcon" class="mr-50"/>
                  Text Area
                </b-dropdown-item>
                <b-dropdown-item @click="addComponent('quiz_test')">
                  <feather-icon icon="HelpCircleIcon" class="mr-50"/>
                  Test/Quiz
                </b-dropdown-item>
                <b-dropdown-item @click="addComponent('essay')">
                  <feather-icon icon="EditIcon" class="mr-50"/>
                  Essay Question
                </b-dropdown-item>
                <b-dropdown-item @click="addComponent('reading')">
                  <feather-icon icon="BookIcon" class="mr-50"/>
                  Reading Material
                </b-dropdown-item>
                <b-dropdown-item @click="addComponent('video')">
                  <feather-icon icon="PlayIcon" class="mr-50"/>
                  Video
                </b-dropdown-item>
              </b-dropdown>
            </div>

            <!-- Component List -->
            <div v-if="form.components && form.components.length > 0" class="components-list">
              <div
                v-for="(component, index) in form.components"
                :key="component.temp_id || component.id"
                class="component-card mb-3"
              >
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                      <feather-icon :icon="getComponentIcon(component.component_type)" class="mr-2"/>
                      <strong>{{ getComponentTypeLabel(component.component_type) }}</strong>
                      <span v-if="component.title" class="ml-2 text-muted">- {{ component.title }}</span>
                    </div>
                    <div>
                      <b-button
                        size="sm"
                        variant="outline-secondary"
                        @click="moveComponent(index, -1)"
                        :disabled="index === 0"
                      >
                        <feather-icon icon="ChevronUpIcon" size="14"/>
                      </b-button>
                      <b-button
                        size="sm"
                        variant="outline-secondary"
                        @click="moveComponent(index, 1)"
                        :disabled="index === form.components.length - 1"
                        class="ml-1"
                      >
                        <feather-icon icon="ChevronDownIcon" size="14"/>
                      </b-button>
                      <b-button
                        size="sm"
                        variant="outline-danger"
                        @click="removeComponent(index)"
                        class="ml-1"
                      >
                        <feather-icon icon="TrashIcon" size="14"/>
                      </b-button>
                    </div>
                  </div>
                  <div class="card-body">
                    <!-- Component Title -->
                    <b-form-group label="Title" class="mb-2">
                      <b-form-input
                          v-model="component.title"
                          placeholder="Component title..."
                      />
                    </b-form-group>

                    <!-- Component Description -->
                    <b-form-group label="Description" class="mb-2">
                      <b-form-textarea
                          v-model="component.description"
                          rows="2"
                          placeholder="Component description..."
                      />
                    </b-form-group>

                    <!-- Component-specific fields -->
                    <div v-if="component.component_type === 'text_input'" class="component-specific">
                      <b-form-group label="Text Content">
                        <b-form-input
                            v-model="component.content.text_value"
                            placeholder="Enter text content..."
                        />
                      </b-form-group>
                    </div>

                    <div v-else-if="component.component_type === 'text_area'" class="component-specific">
                      <b-form-group label="Text Content">
                        <b-form-textarea
                            v-model="component.content.text_value"
                            rows="4"
                            placeholder="Enter detailed text content..."
                        />
                      </b-form-group>
                    </div>

                    <div v-else-if="component.component_type === 'quiz_test'" class="component-specific">
                      <b-form-group label="Select Test Pack">
                        <v-select
                            :options="availableTestPacks"
                            label="name"
                            v-model="component.content.test_pack_id"
                            :reduce="model => model.id"
                            placeholder="Choose a test pack..."
                        />
                      </b-form-group>
                      <b-row>
                        <b-col md="6">
                          <b-form-group label="Time Limit (minutes)">
                            <b-form-input
                                type="number"
                                v-model="component.settings.time_limit_minutes"
                                placeholder="60"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col md="6">
                          <b-form-group label="Attempts Allowed">
                            <b-form-input
                                type="number"
                                v-model="component.settings.attempts_allowed"
                                placeholder="1"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                      <b-form-checkbox v-model="component.settings.randomize_questions">
                        Randomize Questions
                      </b-form-checkbox>
                      <b-form-checkbox v-model="component.settings.show_results_immediately">
                        Show Results Immediately
                      </b-form-checkbox>
                    </div>

                    <div v-else-if="component.component_type === 'essay'" class="component-specific">
                      <b-form-group label="Essay Question Prompt">
                        <b-form-textarea
                            v-model="component.content.essay_prompt"
                            placeholder="Enter the essay question or prompt..."
                            rows="3"
                        />
                      </b-form-group>
                      <b-row>
                        <b-col md="6">
                          <b-form-group label="Min Words">
                            <b-form-input
                                type="number"
                                v-model="component.settings.min_words"
                                placeholder="100"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col md="6">
                          <b-form-group label="Max Words">
                            <b-form-input
                                type="number"
                                v-model="component.settings.max_words"
                                placeholder="500"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                      <b-form-checkbox v-model="component.settings.allow_draft_saving">
                        Allow Draft Saving
                      </b-form-checkbox>
                      <b-form-checkbox v-model="component.settings.show_word_count">
                        Show Word Count
                      </b-form-checkbox>
                    </div>

                    <div v-else-if="component.component_type === 'reading'" class="component-specific">
                      <b-form-group label="Reading Content">
                        <b-form-textarea
                            v-model="component.content.reading_text"
                            rows="6"
                            placeholder="Enter the reading material content..."
                        />
                      </b-form-group>
                    </div>

                    <div v-else-if="component.component_type === 'video'" class="component-specific">
                      <b-form-group label="Video URL">
                        <b-form-input
                            v-model="component.content.video_url"
                            placeholder="https://youtube.com/watch?v=..."
                        />
                      </b-form-group>
                    </div>

                    <!-- Common settings -->
                    <div class="component-settings mt-3 pt-3 border-top">
                      <b-row>
                        <b-col md="6">
                          <b-form-group label="Estimated Duration (minutes)">
                            <b-form-input
                                type="number"
                                v-model="component.estimated_duration_minutes"
                                placeholder="10"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col md="6">
                          <b-form-checkbox
                            v-model="component.is_required"
                            class="mt-4"
                          >
                            Required Component
                          </b-form-checkbox>
                        </b-col>
                      </b-row>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center text-muted py-4">
              <p>No content added yet. Use the "Add Content" button to start building your lesson.</p>
            </div>
          </div>

          <!-- Legacy file attachments for compatibility -->
          <section class="file-attachments-section mb-4 mt-4">
            <h5>File Attachments (Legacy Support)</h5>
            <b-row>
              <b-col md="6">
                <h6>Classwork Files</h6>
                <div class="select-wrapper">
                  <b-form-group label="Select Classwork Files">
                    <v-select
                        v-model="form.classworks"
                        label="file_name"
                        :options="classWorks"
                        multiple
                        :reduce="model => model.id"
                        :close-on-select="false"
                    />
                  </b-form-group>
                  <b-button @click="openModal('classwork_file')" size="sm">
                    Add Classwork File
                  </b-button>
                </div>
              </b-col>
              <b-col md="6">
                <h6>Homework Files</h6>
                <div class="select-wrapper">
                  <b-form-group label="Select Homework Files">
                    <v-select
                        v-model="form.homeworks"
                        label="file_name"
                        :options="homeWorks"
                        multiple
                        :reduce="model => model.id"
                        :close-on-select="false"
                    />
                  </b-form-group>
                  <b-button @click="openModal('homework_file')" size="sm">
                    Add Homework File
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </section>

          <div class="mt-4">
            <b-button v-if="!form.id" type="submit" variant="primary" :disabled="isLoading">
              {{ isLoading ? 'Creating...' : $t('Create Lesson') }}
            </b-button>
            <template v-else>
              <b-button type="submit" variant="primary" :disabled="isLoading">
                {{ isLoading ? 'Saving...' : $t('Update Lesson') }}
              </b-button>
              <b-button class="ml-2" @click="removeEditing" type="reset" variant="warning">
                {{ $t('Cancel Editing') }}
              </b-button>
            </template>
          </div>
        </b-form>
      </validation-observer>
    </div>

    <!-- Search and filters -->
    <div class="custom-search d-flex justify-content-between mt-4">
      <div class="d-flex">
        <b-form-select
            v-model="filterLessonType"
            :options="[{value: '', text: 'All Types'}, ...lessonTypes]"
            class="mr-2"
            @input="fetchLessons"
        />
        <b-form-select
            v-model="filterSubjectId"
            :options="[{value: '', text: 'All Subjects'}, ...subjects.map(s => ({value: s.id, text: s.name}))]"
            class="mr-2"
            @input="fetchLessons"
        />
      </div>
      <b-form-group>
        <div class="d-flex align-items-center">
          <label class="mr-1">Search</label>
          <b-form-input
              v-model="searchTerm"
              placeholder="Search lessons..."
              type="text"
              class="d-inline-block"
          />
        </div>
      </b-form-group>
    </div>

    <!-- table -->
    <vue-good-table
        :columns="columns"
        :rows="lessons"
        :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
        :pagination-options="{
        enabled: true,
        perPage:pageLength
      }"
    >
      <template
          slot="table-row"
          slot-scope="props"
      >
        <!-- Column: Lesson Type -->
        <div
            v-if="props.column.field === 'lesson_type'"
            class="text-nowrap"
        >
          <b-badge
              :variant="props.row.lesson_type === 'control' ? 'warning' : 'primary'"
          >
            {{ props.row.lesson_type === 'control' ? 'Control' : 'Regular' }}
          </b-badge>
        </div>

        <!-- Column: Published -->
        <div
            v-else-if="props.column.field === 'is_published'"
            class="text-nowrap"
        >
          <b-badge
              :variant="props.row.is_published ? 'success' : 'secondary'"
          >
            {{ props.row.is_published ? 'Published' : 'Draft' }}
          </b-badge>
        </div>

        <!-- Column: Components Count -->
        <div
            v-else-if="props.column.field === 'components_count'"
            class="text-nowrap"
        >
          {{ props.row.total_components || 0 }} components
        </div>

        <!-- Column: Action -->
        <span v-else-if="props.column.field === 'action'">
          <span>
            <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
            >
              <template v-slot:button-content>
                <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body align-middle mr-25"
                />
              </template>
              <b-dropdown-item @click="editLesson(props.row.id)">
                <feather-icon
                    icon="Edit2Icon"
                    class="mr-50"
                />
                <span>Edit</span>
              </b-dropdown-item>
              <b-dropdown-item v-if="props.row.lesson_type === 'control'" @click="autoPopulateLesson(props.row.id)">
                <feather-icon
                    icon="RefreshCwIcon"
                    class="mr-50"
                />
                <span>Auto-populate</span>
              </b-dropdown-item>
              <b-dropdown-item @click="deleteLesson(props.row.id)">
                <feather-icon
                    icon="TrashIcon"
                    class="mr-50"
                />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
          slot="pagination-bottom"
          slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">
              Showing 1 to
            </span>
            <b-form-select
                v-model="pageLength"
                :options="['5','10','25','50']"
                class="mx-1"
                @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap "> of {{ props.total }} entries </span>
          </div>
          <div>
            <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Modals -->
    <addClassWorkModal @fileUpdated="addFileToClassWorks" />
    <addHomeWorkModal @fileUpdated="addFileToHomeWorks" />

  </div>
</template>

<script>

import {
  BAlert,
  BBadge,
  BButton,
  BCol,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormCheckbox,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormSelect,
  BFormTextarea,
  BPagination,
  BRow
} from 'bootstrap-vue'
import {VueGoodTable} from 'vue-good-table'
import {ValidationObserver, ValidationProvider} from 'vee-validate'
import {createNamespacedHelpers} from 'vuex';
import vSelect from 'vue-select'
import LessonProgramsApi from '@/api/lessonPrograms'
import StudyingLessonsApi from '@/api/studying-lessons'
import StudyingSubjectsApi from '@/api/studying-subjects'
import addClassWorkModal from "@/components/Modals/addClassWorkModal";
import addHomeWorkModal from "@/components/Modals/addHomeWorkModal";

const {mapState, mapActions} = createNamespacedHelpers('lessons')

export default {
  components: {
    VueGoodTable,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormCheckbox,
    BFormFile,
    BDropdown,
    BDropdownItem,
    BFormInvalidFeedback,
    BForm,
    BButton,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    BRow,
    BCol,
    BAlert,
    addClassWorkModal,
    addHomeWorkModal
  },
  data() {
    return {
      isLoading: false,
      pageLength: 10,
      searchTerm: '',
      filterLessonType: '',
      filterSubjectId: '',
      nextTempId: 1,

      columns: [
        {
          label: 'Title',
          field: 'title',
        },
        {
          label: 'Type',
          field: 'lesson_type',
        },
        {
          label: 'Subject',
          field: 'subject.name',
        },
        {
          label: 'Program',
          field: 'program.name',
        },
        {
          label: 'Order',
          field: 'order',
        },
        {
          label: 'Components',
          field: 'components_count',
        },
        {
          label: 'Status',
          field: 'is_published',
        },
        {
          label: 'Actions',
          field: 'action',
        }
      ],

      form: {
        id: null,
        title: '',
        description: '',
        icon: null,
        subject_id: null,
        program_id: null,
        order: 1,
        lesson_type: 'regular',
        difficulty_level: 'beginner',
        estimated_duration_minutes: null,
        is_published: false,
        auto_populate: true,
        selected_test_packs: [],
        selected_essay_questions: [],
        components: [],
        classworks: [],
        homeworks: []
      },

      lessons: [],
      subjects: [],
      programs: [],
      availableTestPacks: [],
      availableEssayQuestions: [],
      homeWorks: [],
      classWorks: [],

      lessonTypes: [
        {value: 'regular', label: 'Regular Lesson'},
        {value: 'control', label: 'Control Lesson'}
      ],

      difficultyLevels: [
        {value: 'beginner', label: 'Beginner'},
        {value: 'intermediate', label: 'Intermediate'},
        {value: 'advanced', label: 'Advanced'}
      ]
    }
  },

  async mounted() {
    await this.fetchSubjects()
    await this.fetchPrograms()
    await this.fetchLessons()
    await this.fetchFileData()
  },

  computed: {
    filteredPrograms() {
      if (!this.form.subject_id) {
        return this.programs
      }
      return this.programs.filter(program => program.subject_id === this.form.subject_id)
    }
  },

  methods: {
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },

    async fetchSubjects() {
      try {
        const response = await StudyingSubjectsApi.getSubjects()
        this.subjects = response.data
      } catch (error) {
        console.error('Error fetching subjects:', error)
        this.showErrorToast('Failed to load subjects')
      }
    },

    async fetchPrograms() {
      try {
        await this.$store.dispatch('programs/getPrograms');
        this.programs = this.$store.state.programs.programs
      } catch (error) {
        console.error('Error fetching programs:', error)
      }
    },

    async fetchLessons() {
      try {
        const params = {}
        if (this.filterLessonType) params.lesson_type = this.filterLessonType
        if (this.filterSubjectId) params.subject_id = this.filterSubjectId

        const response = await LessonProgramsApi.getLessonPrograms(params)
        this.lessons = response.data
      } catch (error) {
        console.error('Error fetching lessons:', error)
        this.showErrorToast('Failed to load lessons')
      }
    },

    async fetchFileData() {
      try {
        const classWorksResponse = await StudyingLessonsApi.getClassWorks()
        this.classWorks = classWorksResponse.data

        const homeWorksResponse = await StudyingLessonsApi.getHomeWorks()
        this.homeWorks = homeWorksResponse.data
      } catch (error) {
        console.error('Error fetching file data:', error)
      }
    },

    async onLessonTypeChange() {
      if (this.form.lesson_type === 'control' && this.form.subject_id) {
        await this.fetchAvailableResources()
      } else {
        this.availableTestPacks = []
        this.availableEssayQuestions = []
      }

      // Clear components when switching to control type
      if (this.form.lesson_type === 'control') {
        this.form.components = []
      }
    },

    async onSubjectChange() {
      if (this.form.subject_id) {
        await this.fetchAvailableResources()
      }
    },

    onAutoPopulateChange() {
      if (this.form.auto_populate) {
        this.form.selected_test_packs = []
        this.form.selected_essay_questions = []
      }
    },

    async fetchAvailableResources() {
      if (!this.form.subject_id) return

      try {
        // Fetch available test packs
        const testPacksResponse = await LessonProgramsApi.getAvailableTestPacks(this.form.subject_id)
        this.availableTestPacks = testPacksResponse.data

        // Fetch available essay questions
        const essayQuestionsResponse = await LessonProgramsApi.getAvailableEssayQuestions(this.form.subject_id)
        this.availableEssayQuestions = essayQuestionsResponse.data
      } catch (error) {
        console.error('Error fetching available resources:', error)
      }
    },

    async addComponent(componentType) {
      // Check if subject is selected when adding quiz
      if (componentType === 'quiz_test' && !this.form.subject_id) {
        this.showErrorToast('Please select a subject first before adding a quiz')
        return
      }

      // Fetch test packs if adding a quiz and we haven't fetched them yet
      if (componentType === 'quiz_test' && this.form.subject_id && this.availableTestPacks.length === 0) {
        await this.fetchAvailableResources()
      }

      // Set default duration based on component type
      const getDefaultDuration = (type) => {
        switch(type) {
          case 'quiz_test': return 60
          case 'essay': return 30
          case 'video': return 20
          case 'reading': return 15
          default: return 10
        }
      }

      const component = {
        temp_id: `temp_${this.nextTempId++}`,
        component_type: componentType,
        title: '',
        description: '',
        order_index: this.form.components.length + 1,
        is_required: false,
        estimated_duration_minutes: getDefaultDuration(componentType),
        settings: this.getDefaultSettings(componentType),
        content: this.getDefaultContent(componentType)
      }

      this.form.components.push(component)
    },

    getDefaultSettings(componentType) {
      switch(componentType) {
        case 'quiz_test':
          return {
            time_limit_minutes: null,
            attempts_allowed: 1,
            show_results_immediately: true,
            randomize_questions: false,
            require_all_questions: true
          }
        case 'essay':
          return {
            max_words: null,
            min_words: null,
            allow_draft_saving: true,
            show_word_count: true,
            grading_rubric: null
          }
        default:
          return {}
      }
    },

    getDefaultContent(componentType) {
      switch(componentType) {
        case 'text_input':
          return { text_value: '' }
        case 'text_area':
          return { text_value: '' }
        case 'quiz_test':
          return { test_pack_id: null }
        case 'essay':
          return { essay_prompt: '' }
        case 'reading':
          return { reading_text: '' }
        case 'video':
          return { video_url: '' }
        default:
          return {}
      }
    },

    removeComponent(index) {
      this.form.components.splice(index, 1)
      this.updateComponentOrder()
    },

    moveComponent(index, direction) {
      const newIndex = index + direction
      if (newIndex >= 0 && newIndex < this.form.components.length) {
        const component = this.form.components.splice(index, 1)[0]
        this.form.components.splice(newIndex, 0, component)
        this.updateComponentOrder()
      }
    },

    updateComponentOrder() {
      this.form.components.forEach((component, index) => {
        component.order_index = index + 1
      })
    },

    getComponentIcon(componentType) {
      const icons = {
        'text_input': 'TypeIcon',
        'text_area': 'AlignLeftIcon',
        'quiz_test': 'HelpCircleIcon',
        'essay': 'EditIcon',
        'reading': 'BookIcon',
        'video': 'PlayIcon',
        'presentation': 'PresentationIcon',
        'discussion': 'MessageCircleIcon'
      }
      return icons[componentType] || 'FileIcon'
    },

    getComponentTypeLabel(componentType) {
      const labels = {
        'text_input': 'Text Input',
        'text_area': 'Text Area',
        'quiz_test': 'Test/Quiz',
        'essay': 'Essay Question',
        'reading': 'Reading Material',
        'video': 'Video',
        'presentation': 'Presentation',
        'discussion': 'Discussion'
      }
      return labels[componentType] || 'Unknown'
    },

    async createOrUpdateLesson() {
      this.isLoading = true
      try {
        const formData = new FormData()

        // Basic lesson data
        formData.append('title', this.form.title)
        formData.append('description', this.form.description || '')
        formData.append('subject_id', this.form.subject_id)
        formData.append('lesson_type', this.form.lesson_type)
        formData.append('difficulty_level', this.form.difficulty_level)
        // Ensure estimated_duration_minutes is always a valid number
        const duration = parseInt(this.form.estimated_duration_minutes) || 60
        formData.append('estimated_duration_minutes', duration)
        formData.append('is_published', this.form.is_published ? 1 : 0)
        formData.append('order', this.form.order)

        // Icon file
        if (this.form.icon) {
          formData.append('icon', this.form.icon)
        }

        // Program association (legacy)
        if (this.form.program_id) {
          formData.append('program_id', this.form.program_id)
        }

        // Legacy file attachments
        formData.append('classworks', JSON.stringify(this.form.classworks || []))
        formData.append('homeworks', JSON.stringify(this.form.homeworks || []))

        // Control lesson settings
        if (this.form.lesson_type === 'control') {
          formData.append('auto_populate', this.form.auto_populate ? 1 : 0)
          if (!this.form.auto_populate) {
            formData.append('selected_test_packs', JSON.stringify(this.form.selected_test_packs || []))
            formData.append('selected_essay_questions', JSON.stringify(this.form.selected_essay_questions || []))
          }
        }

        // Components for regular lessons
        if (this.form.lesson_type === 'regular' && this.form.components) {
          formData.append('components', JSON.stringify(this.form.components))
        }

        if (this.form.id) {
          // Laravel requires _method for PUT requests with FormData
          formData.append('_method', 'PUT')
          await LessonProgramsApi.updateLessonProgram(this.form.id, formData)
        } else {
          await LessonProgramsApi.createLessonProgram(formData)
        }

        this.showSuccessToast(this.form.id ? 'Lesson updated successfully' : 'Lesson created successfully')
        this.resetForm()
        await this.fetchLessons()

      } catch (error) {
        console.error('Error saving lesson:', error)
        this.showErrorToast(error.response?.data?.error || 'Failed to save lesson')
      } finally {
        this.isLoading = false
      }
    },

    async editLesson(id) {
      try {
        const response = await LessonProgramsApi.getLessonProgram(id)
        const lesson = response.data

        this.form = {
          id: lesson.id,
          title: lesson.title,
          description: lesson.description || '',
          icon: null,
          subject_id: lesson.subject_id,
          program_id: lesson.program_id || null,
          order: lesson.order || 1,
          lesson_type: lesson.lesson_type,
          difficulty_level: lesson.difficulty_level,
          estimated_duration_minutes: lesson.estimated_duration_minutes,
          is_published: lesson.is_published,
          auto_populate: false,
          selected_test_packs: [],
          selected_essay_questions: [],
          components: lesson.components || [],
          classworks: lesson.classwork_files ? lesson.classwork_files.map(f => f.id) : [],
          homeworks: lesson.homework_files ? lesson.homework_files.map(f => f.id) : []
        }

        if (this.form.lesson_type === 'control') {
          await this.fetchAvailableResources()
        }

        // Scroll to form
        this.$el.querySelector('.form').scrollIntoView({ behavior: 'smooth' })

      } catch (error) {
        console.error('Error fetching lesson:', error)
        this.showErrorToast('Failed to load lesson')
      }
    },

    async autoPopulateLesson(id) {
      try {
        await LessonProgramsApi.autoPopulate(id)
        this.showSuccessToast('Lesson auto-populated with tests and essays')
        await this.fetchLessons()
      } catch (error) {
        console.error('Error auto-populating lesson:', error)
        this.showErrorToast('Failed to auto-populate lesson')
      }
    },

    async deleteLesson(id) {
      if (!confirm('Are you sure you want to delete this lesson?')) {
        return
      }

      try {
        await LessonProgramsApi.deleteLessonProgram(id)
        this.showSuccessToast('Lesson deleted successfully')
        await this.fetchLessons()
      } catch (error) {
        console.error('Error deleting lesson:', error)
        this.showErrorToast('Failed to delete lesson')
      }
    },

    openModal(id) {
      this.$root.$emit('bv::show::modal', id)
    },

    addFileToHomeWorks(file) {
      this.homeWorks.unshift(file)
    },

    addFileToClassWorks(file) {
      this.classWorks.unshift(file)
    },

    resetForm() {
      this.form = {
        id: null,
        title: '',
        description: '',
        icon: null,
        subject_id: null,
        program_id: null,
        order: 1,
        lesson_type: 'regular',
        difficulty_level: 'beginner',
        estimated_duration_minutes: null,
        is_published: false,
        auto_populate: true,
        selected_test_packs: [],
        selected_essay_questions: [],
        components: [],
        classworks: [],
        homeworks: []
      }
      this.availableTestPacks = []
      this.availableEssayQuestions = []
      this.$refs.refFormObserver.reset()
    },

    removeEditing() {
      this.resetForm()
    },

    showSuccessToast(message) {
      this.$bvToast.toast(message, {
        title: 'Success',
        variant: 'success',
        solid: true,
      })
    },

    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Error',
        variant: 'danger',
        solid: true,
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.form {
  background: white;
  padding: 2rem;
  border-radius: 0.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.basic-info-section {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;

  h4 {
    margin-bottom: 1.5rem;
    color: #495057;
  }
}

.control-lesson-section {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.375rem;
  border-left: 4px solid #17a2b8;
}

.lesson-components-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.375rem;
  border-left: 4px solid #28a745;
}

.file-attachments-section {
  background: #fefefe;
  padding: 1.5rem;
  border-radius: 0.375rem;
  border-left: 4px solid #6c757d;

  h6 {
    color: #495057;
    margin-bottom: 1rem;
  }
}

.component-card {
  .card {
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;

    .card-header {
      background: #ffffff;
      border-bottom: 1px solid #dee2e6;
      font-weight: 500;
    }

    .card-body {
      background: #ffffff;
    }
  }
}

.select-wrapper {
  margin-bottom: 1.5rem;
}

.component-specific {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
}

.component-settings {
  background: #e9ecef;
  padding: 1rem;
  border-radius: 0.25rem;
}

.alert {
  border-left: 4px solid #17a2b8;
  background: #d1ecf1;
  border-color: #bee5eb;
}

.badge {
  font-size: 0.75rem;
}

.list-group-item {
  border: 1px solid #dee2e6;
  background: #f8f9fa;
  margin-bottom: 0.25rem;
  border-radius: 0.25rem;
}
</style>